import React, { Suspense, lazy } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, Sparkles, Wrench, Store, MessageSquare, Activity, Bot, Zap, Shield, TrendingUp,
  Quote, Workflow, GitBranch, Send, Brain, Clock, DollarSign, Users,
} from 'lucide-react';
import { LANDING } from '@/constants/testIds';
import { HeroSceneSkeleton } from '@/components/hero/HeroSceneSkeleton';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { AgentAvatar } from '@/components/AgentAvatar';
import { Reveal, CountUp } from '@/components/Reveal';
import { AgentNetworkDiagram } from '@/components/AgentNetworkDiagram';
import { LogoCloud } from '@/components/LogoCloud';
import { WhyNow } from '@/components/WhyNow';
import { Architecture } from '@/components/Architecture';
import { Roadmap } from '@/components/Roadmap';
import { TrustSecurity } from '@/components/TrustSecurity';

const HeroScene = lazy(() => import('@/components/hero/HeroScene'));

const Stat = ({ k, v, suffix = '' }) => (
  <div>
    <div className="display text-2xl font-semibold">
      {typeof v === 'number' ? <CountUp to={v} suffix={suffix} /> : v}
    </div>
    <div className="text-xs uppercase tracking-widest text-white/40 mt-1">{k}</div>
  </div>
);

const featureTiles = [
  { icon: Store, title: 'Marketplace', body: 'Hire 18 revenue-grade agents trained on operator playbooks.', color: '#FF6B1A' },
  { icon: Wrench, title: 'Builder', body: 'No-code: name your role, write a prompt, ship in under 60s.', color: '#39FF14' },
  { icon: MessageSquare, title: 'Live Chat', body: 'Stream replies, switch models, retain context across runs.', color: '#FF6B1A' },
  { icon: Activity, title: 'Observability', body: 'Conversations, usage, and plan limits in one dashboard.', color: '#39FF14' },
];

const testimonials = [
  { who: 'Maya R.', role: 'Founder, RampUp', body: 'Velocity SDR booked 23 demos in our first 9 days. This is the agentic stack we were waiting for.' },
  { who: 'Daniel K.', role: 'GTM Lead, NorthSignal', body: 'Closer Coach replaced our $5k/mo enablement vendor. Reps are closing 31% more deals.' },
  { who: 'Priya N.', role: 'Marketing, Hexaflow', body: 'Landing Page Genius writes copy that actually converts. We A/B tested and it won 7/10.' },
];

// Looping "live agent activity" feed (purely visual)
const LIVE_FEED = [
  { who: 'Velocity SDR',        act: 'drafted 12 cold emails',          color: '#FF6B1A' },
  { who: 'Signal Spotter',      act: 'flagged 3 funding-round triggers', color: '#39FF14' },
  { who: 'Landing Page Genius', act: 'shipped 3 hero variants',          color: '#FFB17A' },
  { who: 'Pricing Strategist',  act: 'designed a usage-based tier',      color: '#39FF14' },
  { who: 'Pipeline Hunter',     act: 'scored 47 inbound leads',          color: '#FF6B1A' },
  { who: 'Content Cannon',      act: 'wrote 3 TikTok scripts',           color: '#39FF14' },
  { who: 'Closer Coach',        act: 'rehearsed 6 objection scenarios',  color: '#FFB17A' },
  { who: 'Ops Architect',       act: 'mapped a 7-step SOP',              color: '#FF6B1A' },
  { who: 'Support Concierge',   act: 'cleared 18 tier-1 tickets',        color: '#39FF14' },
];

export default function Landing() {
  const { data } = useQuery({
    queryKey: ['agents', 'featured'],
    queryFn: async () => (await api.get('/agents?featured=true')).data,
  });
  const featured = data?.items?.slice(0, 6) || [];

  return (
    <div>
      {/* =========================== HERO =========================== */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 void-ember pointer-events-none" />
        <div className="absolute inset-0 noise pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 lg:pt-20 lg:pb-28 relative">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6 relative z-10">
              <motion.div
                initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full hairline text-xs text-white/70 mb-6"
              >
                <span className="h-2 w-2 rounded-full bg-[#39FF14] pulse-green" />
                Now live: 18 revenue-grade agents • Build your own in 60s
              </motion.div>
              <motion.h1
                initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.05 }}
                className="display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight"
              >
                The <span className="gradient-text">agentic revenue OS</span><br />
                for operators who ship.
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.1 }}
                className="mt-5 text-base md:text-lg text-white/70 max-w-xl"
              >
                Hire AI agents that close deals, run support, and write copy that converts. Or build your own — no code, no fluff, just revenue.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.15 }}
                className="mt-8 flex flex-wrap items-center gap-3"
              >
                <Link to="/marketplace" data-testid={LANDING.heroPrimaryCta} className="btn-primary inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-medium">
                  Deploy an Agent <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/builder" data-testid={LANDING.heroSecondaryCta} className="btn-secondary inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm">
                  <Wrench className="h-4 w-4" /> Build Your Own
                </Link>
              </motion.div>
              <div className="mt-10 grid grid-cols-3 max-w-md gap-6">
                <Stat k="Agents" v="18" />
                <Stat k="Categories" v="9" />
                <Stat k="Free Trials" v="Unlimited" />
              </div>
            </div>
            <div className="lg:col-span-6 relative h-[440px] sm:h-[540px] lg:h-[620px] rounded-3xl overflow-hidden card-base">
              <div className="absolute inset-0 void-ember" />
              <Suspense fallback={<HeroSceneSkeleton />}>
                <HeroScene />
              </Suspense>
              <div className="absolute top-4 left-4 right-4 flex items-center justify-between text-[10px] text-white/55">
                <div className="flex items-center gap-2 mono uppercase tracking-widest">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#39FF14] pulse-green" /> live mesh
                </div>
                <div className="mono uppercase tracking-widest">orchestrator · v1.0</div>
              </div>
              <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between text-[11px] text-white/65">
                <div className="glass rounded-lg px-3 py-1.5 mono text-[10px]">
                  <span className="text-[#FF6B1A]">▲</span> 5 archetypes · 1 orchestrator <span className="text-[#39FF14]">▼</span>
                </div>
                <div className="mono text-[10px]">packets streaming</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== LIVE AGENT FEED ===================== */}
      <section className="relative border-y border-border/60 bg-[hsl(225,18%,5%)] overflow-hidden">
        <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[hsl(225,18%,5%)] to-transparent z-10 pointer-events-none" />
        <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[hsl(225,18%,5%)] to-transparent z-10 pointer-events-none" />
        <div className="flex gap-10 py-4 whitespace-nowrap will-change-transform"
             style={{ animation: 'marquee 38s linear infinite' }}>
          {[...LIVE_FEED, ...LIVE_FEED, ...LIVE_FEED].map((row, i) => (
            <div key={`feed-${row.who}-${i}`} className="inline-flex items-center gap-3 text-[11px] uppercase tracking-widest text-white/70">
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: row.color, boxShadow: `0 0 12px ${row.color}` }} />
              <span className="mono text-white/55">live</span>
              <span className="text-white/90">{row.who}</span>
              <span className="text-white/45">·</span>
              <span className="text-white/70">{row.act}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Investor-grade additions */}
      <LogoCloud />
      <WhyNow />

      {/* ====================== BENTO VALUE PROPS ====================== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12">
        <Reveal>
          <div className="grid grid-cols-12 gap-4 md:gap-6">
            <div className="col-span-12 lg:col-span-8 card-base card-hover p-6 lg:p-8 relative overflow-hidden">
              <span className="text-[10px] uppercase tracking-widest text-[#FF6B1A]">// from prompt to revenue workflow</span>
              <h2 className="display text-2xl md:text-3xl font-semibold mt-2 max-w-xl leading-tight">
                One platform. Marketplace, Builder, and live Chat — wired together end-to-end.
              </h2>
              <p className="text-white/65 mt-3 max-w-2xl text-sm md:text-base">
                Pick an agent from the marketplace, fork it into the builder, chat-test it live, and route winners into your stack. The whole loop, one keystroke.
              </p>
              <div className="mt-6 grid grid-cols-3 gap-3 max-w-xl">
                {['Hire', 'Tune', 'Deploy'].map((s, i) => (
                  <div key={s} className="glass p-3 rounded-xl text-center">
                    <div className="mono text-[10px] text-white/40">step 0{i + 1}</div>
                    <div className="display text-base mt-1">{s}</div>
                  </div>
                ))}
              </div>
              <div className="absolute right-[-40px] top-[-40px] h-56 w-56 rounded-full bg-[#FF6B1A]/15 blur-3xl" />
              <div className="absolute left-[-30px] bottom-[-50px] h-40 w-40 rounded-full bg-[#39FF14]/10 blur-3xl" />
            </div>

            {featureTiles.map(({ icon: Icon, title, body, color }, i) => (
              <Reveal delay={0.04 * i} key={title} className="col-span-12 sm:col-span-6 lg:col-span-4">
                <motion.div whileHover={{ y: -2 }} className="card-base card-hover p-5 h-full">
                  <div className="h-10 w-10 rounded-xl grid place-items-center hairline mb-3" style={{ boxShadow: `0 0 18px ${color}33` }}>
                    <Icon className="h-5 w-5" style={{ color }} />
                  </div>
                  <div className="display text-lg font-semibold">{title}</div>
                  <p className="text-sm text-white/65 mt-1">{body}</p>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </Reveal>
      </section>

      {/* ====================== HOW IT WORKS (Agentic Flow Diagram) ====================== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          <Reveal className="lg:col-span-5 order-2 lg:order-1">
            <span className="text-[10px] uppercase tracking-widest text-[#39FF14]">// how it works</span>
            <h2 className="display text-2xl md:text-4xl font-semibold mt-2 leading-tight">
              A mesh of agents, <span className="gradient-text">one calm orchestrator.</span>
            </h2>
            <p className="text-white/65 mt-3 max-w-md text-sm md:text-base">
              Every RevCore agent reports up to a central orchestrator that routes context, retains memory, and chains tasks. You see the surface; we run the swarm.
            </p>
            <ul className="mt-6 space-y-4">
              {[
                { icon: Workflow, t: '01 · Route', d: 'The orchestrator parses your prompt, picks the right agent(s), and dispatches.' },
                { icon: Brain, t: '02 · Reason', d: 'Each agent reasons inside its specialty — Sales, Marketing, Ops — using its tuned prompt.' },
                { icon: GitBranch, t: '03 · Respond', d: 'Outputs converge back to the core. You get a clean, decisive answer in seconds.' },
              ].map((s, i) => (
                <Reveal key={s.t} delay={0.08 * i} className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-xl grid place-items-center hairline shrink-0" style={{ boxShadow: '0 0 16px rgba(255,107,26,0.25)' }}>
                    <s.icon className="h-4 w-4 text-[#FF6B1A]" />
                  </div>
                  <div>
                    <div className="mono text-[10px] uppercase tracking-widest text-white/45">{s.t.split(' ·')[0]}</div>
                    <div className="display font-semibold mt-0.5">{s.t.split('· ')[1]}</div>
                    <p className="text-sm text-white/65 mt-1 max-w-md">{s.d}</p>
                  </div>
                </Reveal>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-7 order-1 lg:order-2">
            <div className="card-base p-3 sm:p-6 relative overflow-hidden">
              <div className="absolute right-[-30px] top-[-30px] h-56 w-56 rounded-full bg-[#FF6B1A]/15 blur-3xl pointer-events-none" />
              <div className="absolute left-[-30px] bottom-[-50px] h-44 w-44 rounded-full bg-[#39FF14]/10 blur-3xl pointer-events-none" />
              <div className="relative aspect-[510/440] w-full">
                <AgentNetworkDiagram />
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-4 text-[11px] text-white/55">
                <span className="inline-flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#FF6B1A]" /> outbound packets</span>
                <span className="inline-flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#39FF14]" /> inbound context</span>
                <span className="inline-flex items-center gap-2 mono">8 nodes · 1 orchestrator</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ====================== METRICS STRIP ====================== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <Reveal>
          <div className="card-base p-6 lg:p-8 relative overflow-hidden">
            <div className="absolute -right-12 -top-16 h-56 w-56 rounded-full bg-[#FF6B1A]/15 blur-3xl pointer-events-none" />
            <div className="absolute -left-12 -bottom-16 h-44 w-44 rounded-full bg-[#39FF14]/10 blur-3xl pointer-events-none" />
            <div className="relative grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { v: 137000, k: 'Messages routed', icon: Send, color: '#FF6B1A' },
                { v: 2400, k: 'Custom agents built', icon: Bot, color: '#39FF14' },
                { v: 99, k: 'Avg. agent latency', suffix: '%', icon: Clock, color: '#FFB17A' },
                { v: 31, k: 'Avg lift in close rate', suffix: '%', icon: TrendingUp, color: '#39FF14' },
              ].map((m) => (
                <div key={m.k} className="flex items-center gap-4">
                  <div className="h-10 w-10 rounded-xl grid place-items-center hairline shrink-0" style={{ boxShadow: `0 0 16px ${m.color}33` }}>
                    <m.icon className="h-5 w-5" style={{ color: m.color }} />
                  </div>
                  <div>
                    <div className="display text-2xl font-semibold"><CountUp to={m.v} suffix={m.suffix || ''} /></div>
                    <div className="text-[11px] uppercase tracking-widest text-white/45 mt-0.5">{m.k}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      {/* ====================== FEATURED AGENTS ====================== */}
      <section data-testid={LANDING.featuredCarousel} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="flex items-end justify-between mb-6">
          <div>
            <span className="text-[10px] uppercase tracking-widest text-[#39FF14]">// featured</span>
            <h2 className="display text-2xl md:text-3xl font-semibold mt-1">Hot agents this week</h2>
          </div>
          <Link to="/marketplace" className="btn-ghost inline-flex items-center gap-1 text-sm px-3 py-2 rounded-md">
            See all 18 <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {featured.map((a, i) => (
            <Reveal delay={i * 0.05} key={a.agent_id}>
              <motion.div whileHover={{ y: -3 }}>
                <Link to={`/agents/${a.agent_id}`} className="card-base card-hover p-5 block h-full">
                  <div className="flex items-center gap-3 mb-3">
                    <AgentAvatar name={a.name} size={44} />
                    <div>
                      <div className="display font-semibold leading-tight">{a.name}</div>
                      <div className="text-xs text-white/55">{a.role}</div>
                    </div>
                  </div>
                  <p className="text-sm text-white/65 line-clamp-2">{a.description}</p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-widest text-[#39FF14]">{a.category}</span>
                    <span className="text-xs text-[#FF6B1A] inline-flex items-center gap-1">Try <ArrowRight className="h-3 w-3" /></span>
                  </div>
                </Link>
              </motion.div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ====================== WHY ====================== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="grid lg:grid-cols-3 gap-4">
          {[
            { icon: Zap, t: '60-second deploy', d: 'From idea to live agent in under a minute. No infra, no boilerplate.' },
            { icon: Bot, t: 'Multi-model brain', d: 'GPT-5, Claude Sonnet 4.6, Gemini — we route around outages automatically.' },
            { icon: Shield, t: 'Operator-grade', d: 'Cookie sessions, server-verified auth, plan-aware limits out of the box.' },
            { icon: TrendingUp, t: 'Revenue-focused', d: 'Every agent is tuned for a measurable outcome — leads, replies, deals, MRR.' },
            { icon: Sparkles, t: 'Stunning by default', d: 'A 3D hero, neon glow, and micro-interactions — because brand is a moat.' },
            { icon: Wrench, t: 'Fork everything', d: 'Every marketplace agent is a starting point. Customize, save, ship.' },
          ].map(({ icon: Icon, t, d }, i) => (
            <Reveal delay={i * 0.04} key={t}>
              <motion.div whileHover={{ y: -2 }} className="card-base card-hover p-5 h-full">
                <Icon className="h-5 w-5 text-[#FF6B1A] mb-3" />
                <div className="display text-base font-semibold">{t}</div>
                <p className="text-sm text-white/65 mt-1">{d}</p>
              </motion.div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ====================== SOCIAL PROOF ====================== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <span className="text-[10px] uppercase tracking-widest text-[#FF6B1A]">// what operators say</span>
        <h2 className="display text-2xl md:text-3xl font-semibold mt-1 mb-6">Built for revenue. Loved by operators.</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {testimonials.map((t, i) => (
            <Reveal delay={i * 0.06} key={t.who}>
              <div className="card-base card-hover p-6 h-full">
                <Quote className="h-5 w-5 text-[#39FF14] mb-3" />
                <p className="text-sm text-white/80 leading-relaxed">“{t.body}”</p>
                <div className="mt-4 flex items-center gap-3">
                  <AgentAvatar name={t.who} size={36} />
                  <div>
                    <div className="text-sm font-medium">{t.who}</div>
                    <div className="text-xs text-white/50">{t.role}</div>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ====================== ARCHITECTURE ====================== */}
      <Architecture />

      {/* ====================== TRUST & SECURITY ====================== */}
      <TrustSecurity />

      {/* ====================== ROADMAP ====================== */}
      <Roadmap />

      {/* ====================== FINAL CTA ====================== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <Reveal>
          <div className="relative card-base p-8 lg:p-12 overflow-hidden">
            <div className="absolute -right-10 -top-10 h-64 w-64 rounded-full bg-[#FF6B1A]/15 blur-3xl" />
            <div className="absolute -left-10 -bottom-10 h-56 w-56 rounded-full bg-[#39FF14]/10 blur-3xl" />
            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              <div>
                <span className="text-[10px] uppercase tracking-widest text-[#39FF14]">// ship today</span>
                <h2 className="display text-3xl md:text-4xl font-semibold mt-2 leading-tight">
                  Your first agent is 60 seconds away.
                </h2>
                <p className="text-white/65 mt-3 max-w-md">
                  Free to try. No credit card. Real LLM responses. The agentic edge your competition will quietly envy.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 md:justify-end">
                <Link to="/marketplace" data-testid={LANDING.finalCta} className="btn-primary inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-medium">
                  Start Free <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/pricing" className="btn-secondary inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm">
                  View pricing
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
